import { JSX } from 'react';
import {
    List,
    TextField,
    TopToolbar,
    CreateButton,
    ExportButton,
    Datagrid,
    EditButton,
    SelectField,
    SearchInput,
    SelectInput,
    AutocompleteInput,
    FunctionField,
    useGetList
} from 'react-admin';
import { CsvImportButton } from "../import/CsvImportButton";
import { CsvExportForImportButton } from "../import/CsvExportForImportButton";
import { nokEmailTemplateImport } from "../import/configs/nokEmailTemplateImport";
import { nokEmailTemplateStatuses } from "../../defaults/defaults";
import { useASettingByCode } from "../global-hooks/default-system-hooks";
import { NokEmailTemplate } from "../../types/entities";

type Choice = { id: string, name: string };

const types: Choice[] = [
    { id: 'email_wrapper', name: 'Email Wrapper' },
    { id: 'ms_email_body', name: 'Mystery Shopper Message Body' },
    { id: 'ms_form_message', name: 'Mystery Shopper Form Message' },
    { id: 'cs_email_body', name: 'Compliance Sniffer Message Body' },
    { id: 'cs_form_message', name: 'Compliance Sniffer Form Message' },
];

// HQ-API's `NokEmailTemplateTargetFilter` matches these exactly: `either` is a template with no
// sender type, not every template.
const senderTypes: Choice[] = [
    { id: 'company', name: 'Company senders' },
    { id: 'consumer', name: 'Consumer senders' },
    { id: 'either', name: 'Either' },
];

/** The first two names of a list, then how many more, so a long list keeps the row one line. */
function shortList(names: string[]): string {
    return names.length > 2 ? names.slice(0, 2).join(', ') + ' +' + (names.length - 2) + ' more' : names.join(', ');
}

const ListActions: () => JSX.Element = (): JSX.Element => {
    return(
        <TopToolbar>
            {/* Was isAdmin, which showed this button to ROLE_ADMIN while HQ-API held the write to
                super admin and above, so an admin got a create form the POST then refused.
                HQ_Nok::faux_nokkers is the API's own tier, which closes the gap. */}
                <CreateButton />
                <CsvImportButton config={ nokEmailTemplateImport } />
                <CsvExportForImportButton config={ nokEmailTemplateImport } />
            {/*<FilterButton />*/}
            {/*<ExportButton />*/}
        </TopToolbar>
    );
}


export const NokEmailTemplateList: () => JSX.Element = (): JSX.Element => {
    const { data: industryData } = useGetList('industries', { pagination: { page: 1, perPage: 500 } });
    const industries: Choice[] = (industryData ?? []).map((item): Choice => ({ id: String(item.id), name: item.name }));
    const industryNames: Map<string, string> = new Map(industries.map((industry: Choice): [string, string] => [industry.id, industry.name]));

    // The same list the edit form's tag picker offers.
    const { settingValue } = useASettingByCode('default_department_tags');
    let tags: Choice[] = [];
    try {
        const entries: unknown = JSON.parse(settingValue ?? '[]');
        tags = Array.isArray(entries)
            ? entries.map((entry: { key: string, value: string }): Choice => ({ id: entry.key, name: entry.value }))
            : [];
    } catch {
        tags = [];
    }
    const tagNames: Map<string, string> = new Map(tags.map((tag: Choice): [string, string] => [tag.id, tag.name]));

    // A name search, as the other HQ lists have; HQ-API's `name` filter is a partial match.
    // Industry, sender type and tag are HQ-API's `NokEmailTemplateTargetFilter`.
    const listFilters: JSX.Element[] = [
        <SearchInput key="name" source="name" alwaysOn />,
        <SelectInput key="status" source="status" choices={ nokEmailTemplateStatuses } alwaysOn />,
        <SelectInput key="type" source="type" choices={ types } alwaysOn />,
        <AutocompleteInput key="industry" source="industry" label="Industry" choices={ industries } sx={ { minWidth: 240 } } alwaysOn />,
        <SelectInput key="senderType" source="senderType" label="Sender type" choices={ senderTypes } alwaysOn />,
        <AutocompleteInput key="tag" source="tag" label="Tag" choices={ tags } sx={ { minWidth: 200 } } alwaysOn />,
    ];

    return (
        <List actions={ <ListActions/> } filters={ listFilters } >
            <Datagrid bulkActionButtons={ false } >
                <TextField source="id" />
                <TextField source="name" />
                <SelectField source="type" choices={ types } />
                <SelectField source="status" choices={ nokEmailTemplateStatuses } />
                <FunctionField label="Industries" sortable={ false } render={ (record: NokEmailTemplate): string =>
                    shortList((record.industries ?? []).map((id: number): string => industryNames.get(String(id)) ?? '#' + id))
                } />
                <FunctionField label="Sender type" sortable={ false } render={ (record: NokEmailTemplate): string =>
                    record.isBusiness === true ? 'Company' : (record.isBusiness === false ? 'Consumer' : 'Either')
                } />
                <FunctionField label="Tags" sortable={ false } render={ (record: NokEmailTemplate): string =>
                    shortList((record.tags ?? []).map((tag: string): string => tagNames.get(tag) ?? tag))
                } />
                <EditButton />
            </Datagrid>
        </List>
    );
}
